/**
 * PATH: apps/qa/src/pages/ncr-detail/NCRDetailsSidebar.tsx
 * PURPOSE: NCR details sidebar with metadata and status transition actions
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ncrApi } from '@/lib/api'
import { formatDateTime } from '@/lib/utils'
import { FileText, MapPin, User, Calendar, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react'

interface NCRDetailsSidebarProps {
  ncr: Record<string, unknown>
}

export function NCRDetailsSidebar({ ncr }: NCRDetailsSidebarProps) {
  const queryClient = useQueryClient()
  const id = ncr.id as string
  const status = ncr.status as string

  const startWorkMutation = useMutation({
    mutationFn: async () => ncrApi.startWork(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-ncr', id] })
      queryClient.invalidateQueries({ queryKey: ['qa-ncrs'] })
    },
  })

  const readyForReviewMutation = useMutation({
    mutationFn: async () => ncrApi.markReadyForReview(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-ncr', id] })
      queryClient.invalidateQueries({ queryKey: ['qa-ncrs'] })
    },
  })

  const closeMutation = useMutation({
    mutationFn: async () => ncrApi.closeNCR(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-ncr', id] })
      queryClient.invalidateQueries({ queryKey: ['qa-ncrs'] })
    },
  })

  return (
    <div className="space-y-6">
      {/* Details */}
      <div className="bg-card rounded-xl shadow-sm border border-border p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Details</h2>
        <dl className="space-y-4">
          {ncr.job && (
            <div>
              <dt className="text-sm text-muted-foreground flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Job</dt>
              <dd className="text-foreground font-medium text-sm">{ncr.job_id ? `Job #${ncr.job_id}` : `Job #${ncr.job}`}</dd>
            </div>
          )}
          {ncr.area && (
            <div>
              <dt className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Area</dt>
              <dd className="text-foreground font-medium text-sm">{(ncr.area_name as string) || (ncr.area as string)}</dd>
            </div>
          )}
          {ncr.raised_by_name && (
            <div>
              <dt className="text-sm text-muted-foreground flex items-center gap-1"><User className="w-3.5 h-3.5" /> Raised By</dt>
              <dd className="text-foreground font-medium text-sm">{ncr.raised_by_name as string}</dd>
            </div>
          )}
          <div>
            <dt className="text-sm text-muted-foreground flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Created</dt>
            <dd className="text-foreground text-sm">{ncr.created_at ? formatDateTime(ncr.created_at as string) : 'Unknown'}</dd>
          </div>
        </dl>
      </div>

      {/* Actions */}
      {status !== 'closed' && (
        <div className="bg-card rounded-xl shadow-sm border border-border p-6 space-y-3">
          <h2 className="text-lg font-semibold text-foreground mb-4">Actions</h2>

          {status === 'open' && (
            <button onClick={() => startWorkMutation.mutate()} disabled={startWorkMutation.isPending}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-amber-600 text-white font-medium hover:bg-amber-700 transition-colors disabled:opacity-50">
              {startWorkMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />} Start Work
            </button>
          )}

          {status === 'in_progress' && (
            <button onClick={() => readyForReviewMutation.mutate()} disabled={readyForReviewMutation.isPending}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-700 transition-colors disabled:opacity-50">
              {readyForReviewMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Mark Ready for Review
            </button>
          )}

          {status === 'ready_for_review' && (
            <button onClick={() => closeMutation.mutate()} disabled={closeMutation.isPending}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-muted text-foreground font-medium hover:bg-muted/80 transition-colors disabled:opacity-50">
              {closeMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Close NCR
            </button>
          )}
        </div>
      )}
    </div>
  )
}
